import { useEffect, useState } from "react";
import { db } from "../firebase";
import {
  collection,
  query,
  where,
  onSnapshot,
  doc,
  runTransaction,
  addDoc,
  serverTimestamp,
} from "firebase/firestore";

function AdminWithdraw() {
  const [withdraws, setWithdraws] = useState([]);
  const [loadingIds, setLoadingIds] = useState([]);

  useEffect(() => {
    const q = query(
      collection(db, "withdraw_requests"),
      where("status", "==", "pending")
    );

    const unsub = onSnapshot(q, (snap) => {
      let data = snap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      }));

      data.sort(
        (a, b) =>
          (b.createdAt?.seconds || 0) -
          (a.createdAt?.seconds || 0)
      );

      setWithdraws(data);
    });

    return () => unsub();
  }, []);

  const copyText = (text) => {
    navigator.clipboard.writeText(text);
  };

  // ✅ APPROVE
  const approveWithdraw = async (w) => {
    const { id, userId, amount } = w;

    setLoadingIds((prev) => [...prev, id]);

    try {
      const withdrawRef = doc(db, "withdraw_requests", id);

      await runTransaction(db, async (transaction) => {
        const snap = await transaction.get(withdrawRef);

        if (!snap.exists() || snap.data().status !== "pending") {
          throw "Already processed";
        }

        transaction.update(withdrawRef, {
          status: "approved",
          approvedAt: serverTimestamp(),
        });
      });

      await addDoc(
        collection(db, "users", userId, "notifications"),
        {
          title: "Withdrawal Approved ✅",
          body: `₹${amount} sent to your UPI`,
          seen: false,
          createdAt: serverTimestamp(),
        }
      );

    } catch (e) {
      console.error(e);
    }

    setLoadingIds((prev) => prev.filter((x) => x !== id));
  };

  // ❌ REJECT
  const rejectWithdraw = async (w) => {
    const { id, userId, amount } = w;

    setLoadingIds((prev) => [...prev, id]);

    try {
      const userRef = doc(db, "users", userId);
      const withdrawRef = doc(db, "withdraw_requests", id);

      await runTransaction(db, async (transaction) => {
        const withdrawSnap = await transaction.get(withdrawRef);
        const userSnap = await transaction.get(userRef);

        if (!withdrawSnap.exists() || withdrawSnap.data().status !== "pending") {
          throw "Already processed";
        }

        const wager = userSnap.data()?.wager || 0;

        transaction.update(withdrawRef, {
          status: "rejected",
          rejectedAt: serverTimestamp(),
        });

        transaction.update(userRef, {
          wager: wager + amount,
        });

        const txnRef = doc(collection(userRef, "transactions"));

        transaction.set(txnRef, {
          amount,
          type: "credit",
          status: "failed",
          description: "Withdrawal Rejected (Refunded)",
          timestamp: serverTimestamp(),
        });
      });

      await addDoc(
        collection(db, "users", userId, "notifications"),
        {
          title: "Withdrawal Rejected ❌",
          body: `₹${amount} refunded to wallet`,
          seen: false,
          createdAt: serverTimestamp(),
        }
      );

    } catch (e) {
      console.error(e);
    }

    setLoadingIds((prev) => prev.filter((x) => x !== id));
  };

  return (
    <div style={container}>
      <h1 style={title}>💸 Withdraw Requests</h1>

      {withdraws.length === 0 ? (
        <p style={{ opacity: 0.6 }}>No pending withdraws</p>
      ) : (
        <div style={grid}>
          {withdraws.map((w) => {
            const loading = loadingIds.includes(w.id);

            return (
              <div key={w.id} style={card}>
                <h2 style={amount}>₹{w.amount}</h2>

                <p style={user}>👤 {w.userId}</p>

                {w.upiId && (
                  <div style={box}>
                    <span>{w.upiId}</span>
                    <button
                      style={copyBtn}
                      onClick={() => copyText(w.upiId)}
                    >
                      📋
                    </button>
                  </div>
                )}

                {w.createdAt?.seconds && (
                  <p style={date}>
                    {new Date(
                      w.createdAt.seconds * 1000
                    ).toLocaleString()}
                  </p>
                )}

                <div style={btnRow}>
                  <button
                    style={{
                      ...approveBtn,
                      opacity: loading ? 0.6 : 1,
                    }}
                    disabled={loading}
                    onClick={() => approveWithdraw(w)}
                  >
                    {loading ? "Processing..." : "Approve"}
                  </button>

                  <button
                    style={{
                      ...rejectBtn,
                      opacity: loading ? 0.6 : 1,
                    }}
                    disabled={loading}
                    onClick={() => rejectWithdraw(w)}
                  >
                    {loading ? "Processing..." : "Reject"}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* 🔥 STYLES */

const container = {
  padding: 20,
  color: "white",
  background: "linear-gradient(135deg, #0F1221, #0A0D1A)",
  minHeight: "100vh",
};

const title = {
  marginBottom: 20,
};

const grid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
  gap: 20,
};

const card = {
  background: "#1A1F2E",
  padding: 18,
  borderRadius: 12,
  border: "1px solid rgba(255,255,255,0.05)",
};

const amount = {
  fontSize: 22,
  marginBottom: 6,
};

const user = {
  fontSize: 13,
  opacity: 0.7,
  marginBottom: 10,
};

const box = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  background: "#222842",
  padding: 8,
  borderRadius: 8,
  marginBottom: 10,
};

const copyBtn = {
  background: "transparent",
  border: "none",
  color: "#fff",
  cursor: "pointer",
};

const date = {
  fontSize: 12,
  opacity: 0.5,
  marginBottom: 10,
};

const btnRow = {
  display: "flex",
  gap: 10,
};

const approveBtn = {
  flex: 1,
  background: "#16a34a",
  border: "none",
  padding: "10px",
  borderRadius: 8,
  color: "#fff",
  cursor: "pointer",
};

const rejectBtn = {
  flex: 1,
  background: "#dc2626",
  border: "none",
  padding: "10px",
  borderRadius: 8,
  color: "#fff",
  cursor: "pointer",
};

export default AdminWithdraw;